import { useState, useMemo, useCallback } from 'react';
import { useAtom } from 'jotai';
import { TreeView } from '@localagent/ui/components/tree-view';
import type { TreeNode, NodeRenderProps } from '@localagent/ui/components/tree-view';
import { useOrgChart } from '../hooks/useOrgChart';
import { errorWithNothingCachedToShow } from '../lib/query-error';
import { selectedPositionIdAtom } from '../stores/orgchart/atoms';
import { PositionNode } from '../components/orgchart/PositionNode';
import { PositionDetail } from '../components/orgchart/PositionDetail';
import {
  EMPTY_ORG_CHART_PROMPTS,
  OrgChartEmptyState,
} from '../components/orgchart/OrgChartEmptyState';
import { usePublishViewSnapshot } from '../components/shogun/usePublishViewSnapshot';
import { expandedOrgRecords } from '../lib/view-snapshot-helpers';
import type { OrgTreeNode } from '../types/orgchart';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

function orgTreeToTreeNodes(node: OrgTreeNode): TreeNode {
  return {
    id: node.position.id,
    label: node.position.title,
    data: {
      role: node.position.role,
      userId: node.position.user_id,
      personaId: node.position.persona_id,
    },
    children: node.children.map(orgTreeToTreeNodes),
  };
}

function findPositionInTree(node: OrgTreeNode, id: string): OrgTreeNode | null {
  if (node.position.id === id) return node;
  for (const child of node.children) {
    const found = findPositionInTree(child, id);
    if (found) return found;
  }
  return null;
}

/**
 * The org chart SHOWS the structure and is changed only by asking the docked
 * agent (app-shell ADR-013): no add-report control, no create
 * dialog, no delete button. The route's `handle` supplies the bottom dock
 * (app-shell ADR-016).
 */
export function OrgChartView() {
  useCatalogSubscription('orgchart');
  useCatalogSubscription('work');
  const { data: chart, isLoading, error } = useOrgChart();
  // A failed REFETCH keeps prior data in v5, so only a read with nothing cached
  // may replace the tree. Live orgchart writes arrive via the catalog holder.
  const chartError = errorWithNothingCachedToShow(chart, error);
  const [selectedId, setSelectedId] = useAtom(selectedPositionIdAtom);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const treeData = useMemo<TreeNode[]>(() => {
    if (!chart?.root) return [];
    return [orgTreeToTreeNodes(chart.root)];
  }, [chart]);

  // Auto-expand root on first load
  useMemo(() => {
    if (chart?.root && expandedIds.size === 0) {
      const ids = new Set<string>();
      ids.add(chart.root.position.id);
      for (const child of chart.root.children) {
        ids.add(child.position.id);
      }
      setExpandedIds(ids);
    }
  }, [chart?.root]);

  const selectedNode = useMemo(() => {
    if (!chart?.root || !selectedId) return null;
    return findPositionInTree(chart.root, selectedId);
  }, [chart, selectedId]);

  const emptyChart = !isLoading && !chartError && !chart?.root;

  const snapshot = useMemo(() => {
    const { counts, visible } = expandedOrgRecords(chart?.root, expandedIds);
    return {
      route: '/orgchart',
      context: {
        surface: 'orgchart',
        primary_type: 'position',
        counts,
        visible,
        ...(selectedNode
          ? {
              selected: {
                type: 'position',
                id: selectedNode.position.id,
                label: selectedNode.position.title,
              },
            }
          : {}),
      },
      ...(emptyChart ? { starterPrompts: EMPTY_ORG_CHART_PROMPTS } : {}),
    };
  }, [chart?.root, expandedIds, selectedNode, emptyChart]);
  usePublishViewSnapshot(snapshot);

  const handleExpand = useCallback((id: string) => {
    setExpandedIds((prev: Set<string>) => new Set([...prev, id]));
  }, []);

  const handleCollapse = useCallback((id: string) => {
    setExpandedIds((prev: Set<string>) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  return (
    <div className="flex h-full" data-testid="orgchart-view">
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b bg-background px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold">Org Chart</h1>
              {chart && (
                <span className="text-sm text-muted-foreground">
                  {chart.total_positions} position{chart.total_positions !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tree area */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading && (
            <LoadingState label="Loading org chart..." className="h-full" />
          )}

          {chartError && (
            <div className="flex h-full items-center justify-center">
              <div className="text-destructive" data-testid="orgchart-error">
                Failed to load org chart: {chartError.message}
              </div>
            </div>
          )}

          {emptyChart && (
            <div className="flex h-full items-center justify-center">
              <OrgChartEmptyState />
            </div>
          )}

          {chart?.root && (
            <TreeView
              data={treeData}
              selectedId={selectedId ?? undefined}
              onSelect={setSelectedId}
              expandedIds={expandedIds}
              onExpand={handleExpand}
              onCollapse={handleCollapse}
              renderNode={(node: TreeNode, props: NodeRenderProps) => (
                <PositionNode node={node} props={props} />
              )}
              className="max-w-2xl"
            />
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedNode && (
        <div className="w-80 flex-shrink-0">
          <PositionDetail
            position={selectedNode.position}
            childCount={selectedNode.children.length}
            onClose={() => setSelectedId(null)}
          />
        </div>
      )}
    </div>
  );
}
